import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { GlobalSearchService, VwGlobalSearchResult } from '../../../services/globalSearch.service';

interface VwSearchGroup {
  category: string;
  items: VwGlobalSearchResult[];
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

// Header search bar — visible only to account-tier roles (see the
// *ngIf around <app-global-search> in default-layout.component.html); the
// backend independently enforces the same boundary by requiring/validating
// accountId for anyone who isn't SUPER_ADMIN, so this is defense in depth,
// not the only gate.
@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @Input() accountId: string | number;

  query = '';
  loading = false;
  open = false;
  activeIndex = -1;
  groups: VwSearchGroup[] = [];
  flatResults: VwGlobalSearchResult[] = [];
  recentSearches: string[] = [];

  private static readonly MAX_RECENT = 6;
  private input$ = new Subject<string>();
  private sub: Subscription;

  constructor(private searchService: GlobalSearchService, private router: Router, private elRef: ElementRef) {}

  ngOnInit(): void {
    this.recentSearches = this.readRecentSearches();
    this.sub = this.input$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(term => {
          if (term.length < MIN_QUERY_LENGTH) {
            return of([] as VwGlobalSearchResult[]);
          }
          this.loading = true;
          return this.searchService.search(term, this.accountId).pipe(catchError(() => of([] as VwGlobalSearchResult[])));
        })
      )
      .subscribe(results => {
        this.loading = false;
        this.setResults(results || []);
      });
  }

  onInput(value: string): void {
    this.query = value;
    this.activeIndex = -1;
    const term = value.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      this.groups = [];
      this.flatResults = [];
      this.open = term.length > 0;
      return;
    }
    this.open = true;
    this.input$.next(term);
  }

  onFocus(): void {
    // Also opens on an empty, never-typed-in box so recent searches are
    // reachable with a single click/tap, not just after already typing.
    if (this.flatResults.length || this.query.trim().length > 0 || this.recentSearches.length > 0) {
      this.open = true;
    }
  }

  selectRecent(term: string): void {
    this.query = term;
    this.open = true;
    this.input$.next(term);
  }

  clearRecentSearches(event: Event): void {
    event.stopPropagation();
    this.recentSearches = [];
    this.writeRecentSearches([]);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.open = false;
      return;
    }
    if (!this.open || !this.flatResults.length) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.flatResults.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.select(this.flatResults[this.activeIndex]);
    }
  }

  select(result: VwGlobalSearchResult): void {
    this.rememberSearch(this.query.trim());
    this.open = false;
    this.query = '';
    this.groups = [];
    this.flatResults = [];
    // Guest is the only entity with a real read-only "/:id" detail route
    // (guestDetails/:id -> GuestDetailsComponent). Other categories share
    // that same list url without an id-addressable detail view, so they
    // land on the list itself rather than a route that doesn't exist.
    // Note: Notification/SecondaryGuestNotification results also point at
    // guestDetails, but their id is a notification id, not a guest id —
    // checking entityType exactly (not the shared url/category) keeps
    // those from being sent to the wrong record.
    if (result.entityType === 'Guest') {
      this.router.navigate([result.url, result.id]);
    } else {
      this.router.navigate([result.url], { queryParams: { q: result.name } });
    }
  }

  isActive(result: VwGlobalSearchResult): boolean {
    return this.flatResults.indexOf(result) === this.activeIndex;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  private recentSearchesKey(): string {
    return 'vw-recent-searches:' + (this.accountId || 'global');
  }

  private readRecentSearches(): string[] {
    try {
      const raw = localStorage.getItem(this.recentSearchesKey());
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  private writeRecentSearches(terms: string[]): void {
    try {
      localStorage.setItem(this.recentSearchesKey(), JSON.stringify(terms));
    } catch (e) {
      // localStorage unavailable (private browsing, quota) — recent
      // searches just won't persist across reloads, nothing else breaks.
    }
  }

  private rememberSearch(term: string): void {
    if (!term) {
      return;
    }
    const deduped = [term, ...this.recentSearches.filter(t => t.toLowerCase() !== term.toLowerCase())];
    this.recentSearches = deduped.slice(0, GlobalSearchComponent.MAX_RECENT);
    this.writeRecentSearches(this.recentSearches);
  }

  private setResults(results: VwGlobalSearchResult[]): void {
    this.flatResults = results;
    const order: string[] = [];
    const byCategory = new Map<string, VwGlobalSearchResult[]>();
    results.forEach(r => {
      const key = r.category || 'Other';
      if (!byCategory.has(key)) {
        byCategory.set(key, []);
        order.push(key);
      }
      byCategory.get(key).push(r);
    });
    this.groups = order.map(category => ({ category, items: byCategory.get(category) }));
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
